<template>
  <Layout>
    <main v-background-color="colorBackground">
      <section
        :class="$style['success-section']"
        v-background-color="colorBackground"
      >
        <v-message
          :logo="logo"
          headingText="Thank You"
          detailText="Your payment was successful. We’ve emailed your receipt. We’ll let your server know that you’ve paid."
          :colorFontHeading="colorFontHeading"
          :colorFont="colorFontBody"
          :fontDetailHeading="fontDetailHeading"
          :fontDetailBody="fontDetailBody"
        >
          <div :class="$style['button-wrapper']">
            <v-button
              hasContainer
              :class="{
                [$style['button']]: true,
                [$style['expanded']]: expanded,
              }"
              buttonText="Your Receipt"
              :colorButton="colorSecondary"
              :colorButtonText="colorFontButton"
              :fontButton="fontDetailHeading"
              @buttonClick="handleExpand"
              :expandable="true"
              :expand="expanded"
            ></v-button>
          </div>
        </v-message>
        <transition
          name="staggered-fade"
          tag="div"
          :css="false"
          @before-enter="beforeEnter"
          @enter="enter"
          @leave="leave"
        >
          <div v-show="expanded" :class="$style['receipt-items']">
            <bill-items
              v-if="billItems"
              :colorFontBody="colorFontBody"
              :fontBody="fontDetailBody"
              :items="billItems"
            />
            <div :class="$style['totals']">
              <div :class="$style['item']">
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >Order Total:</span
                >
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >£{{ billTotalFormatted }}</span
                >
              </div>
              <div :class="$style['item']" v-if="showPromotion">
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >Promotion:</span
                >
                <span v-color="colorFontBody" v-font-style="fontDetailBody">
                  {{ discountFormatted !== '0.00' ? '-' : '' }} £{{
                    discountFormatted
                  }}
                </span>
              </div>
              <div :class="$style['item']">
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >Tip added:</span
                >
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >£{{ gratuityAmountFormatted }}</span
                >
              </div>
              <div :class="$style['item']">
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >Grand Total:</span
                >
                <span v-color="colorFontBody" v-font-style="fontDetailBody"
                  >£{{ finalBalanceFormatted }}</span
                >
              </div>
            </div>
          </div>
        </transition>
      </section>
      <section
        v-if="surveyLink"
        :class="$style['feedback-section']"
        v-background-color="'#fff'"
      >
        <v-message
          headingText="How did we do?
                      Leave your feedback"
          detailText="We hope to see you again soon. 
                      There is always more to discover"
          :colorFontHeading="colorFontHeading"
          :colorFont="colorFontBody"
          :fontDetailHeading="fontDetailHeading"
          :fontDetailBody="fontDetailBody"
        >
          <div :class="$style['button-wrapper']">
            <v-button
              hasContainer
              :class="$style['button']"
              buttonText="Take The Survey"
              :colorButton="colorPrimary"
              :colorButtonText="colorFontButton"
              :fontButton="fontDetailHeading"
              @buttonClick="goToSurvey(surveyLink)"
            ></v-button>
          </div>
        </v-message>
        <base-link :to="{ path: '/terms-and-conditions' }">
          <p
            :class="$style['terms-link']"
            v-color="colorFontBody"
            v-font-style="fontDetailBody"
            >Terms and conditions</p
          >
        </base-link>
      </section>
      <section v-if="!surveyLink">
        <base-link :to="{ path: '/terms-and-conditions' }">
          <p
            :class="$style['terms-link']"
            v-color="colorFontBody"
            v-font-style="fontDetailBody"
            >Terms and conditions</p
          >
        </base-link>
      </section>
    </main>
  </Layout>
</template>
<script>
import velocity from 'velocity-animate'
import { mapActions, mapGetters } from 'vuex'
import appConfig from '@src/app.config'
import Layout from '@layouts/main.vue'
import VMessage from '@components/v-message.vue'
import VButton from '@components/v-button.vue'
import BillItems from '@components/bill-items.vue'

export default {
  page: {
    title: appConfig.title,
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout, VMessage, VButton, BillItems },
  data() {
    return {
      expanded: false,
    }
  },
  computed: {
    ...mapGetters('contentful', [
      'colorBackground',
      'colorPrimary',
      'colorSecondary',
      'colorFontBody',
      'colorFontButton',
      'colorFontHeading',
      'fontDetailBody',
      'fontDetailHeading',
      'fontDetailButton',
      'logo',
      'contentfulLoadingStatus',
    ]),
    ...mapGetters('payment', [
      'paymentResult',
      'surveyLink',
      'billItems',
      'total',
      'totalIncludingDiscount',
      'totalIncludingDiscountAndGratuity',
      'discount',
      'gratuityAmount',
    ]),
    billTotalFormatted() {
      return parseFloat(this.total || 0).toFixed(2)
    },
    discountFormatted() {
      return parseFloat(this.discount || 0).toFixed(2)
    },
    gratuityAmountFormatted() {
      return parseFloat(this.gratuityAmount || 0).toFixed(2)
    },
    finalBalanceFormatted() {
      return parseFloat(this.totalIncludingDiscountAndGratuity || 0).toFixed(2)
    },
    showPromotion() {
      return this.discount !== 0
    },
  },
  methods: {
    ...mapActions('ui', ['setPageLoadingStatus']),
    init() {
      this.setPageLoadingStatus('loading')

      if (!this.paymentResult) {
        return this.$router.replace('/')
      }

      if (this.paymentResult === 'failure') {
        return this.$router.replace('/failure')
      }

      this.setPageLoadingStatus('loaded')
    },
    goToSurvey(destination) {
      window.open(destination, '_blank')
    },
    // this really should be extracted to its own component since its basically the same as v-collapse
    handleExpand() {
      this.expanded = !this.expanded
    },
    beforeEnter(el) {
      const array = [...el.children]
      array.forEach((item) => {
        item.style.opacity = 0
        item.style.height = 0
        item.style.overflow = 'hidden'
      })
    },
    enter(el) {
      const array = [...el.children]
      array.forEach((item, index) => {
        const delay = index * 150
        setTimeout(() => {
          velocity(item, { opacity: 1, height: '100%' })
        }, delay)
      })
    },
    leave(el) {
      const array = [...el.children]
      array.forEach((item, index) => {
        const delay = (array.length - index) * 150
        setTimeout(() => {
          velocity(item, { opacity: 0, height: 0 })
        }, delay)
      })
    },
  },
  watch: {
    contentfulLoadingStatus(newVal) {
      if (newVal === 'loaded') {
        this.init()
      }
    },
  },
  created() {
    this.init()
  },
}
</script>

<style lang="scss" module>
@import '@design';

main {
  // padding: 12px 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.success-section {
  padding: 0 16px;
  padding-top: 12px;
  flex: 1;
}

.feedback-section {
  padding: 0 40px;
  padding-bottom: 12px;
  padding-top: 34px;
  flex: 1;
}

.button {
  margin-bottom: 36px;

  &.expanded {
    margin-bottom: 0;
  }
}

.receipt-items {
  padding-bottom: 16px;
}

.totals {
  margin: 0;
  padding: 0 0 8px;
}

.item {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 12px 0;

  span {
    font-size: 14px;
  }
}

.terms-link {
  padding-bottom: 16px;
  font-size: 12px;
  text-align: center;
  text-decoration: underline;
}
</style>
